// =============================================================================
// io/parsers/zip.ts — just enough ZIP to open an XLSX, with no dependency.
// -----------------------------------------------------------------------------
// An `.xlsx` is a ZIP of XML parts. The XML parsers were ported months ago and
// are parity-tested; this is the piece that was missing, and its absence is why
// the import refused every `.xlsx` with "needs unzipping".
//
// ── NO LIBRARY, AND THAT IS THE OLD CLIENT'S CALL TOO ──
//
// `DecompressionStream('deflate-raw')` is in every browser this app supports and
// does the actual inflating, so the only thing left to write is the container
// format. Beta says the same in its own header: "No external libs."
//
// ── ⚠ THIS READS THE CENTRAL DIRECTORY. BETA WALKS LOCAL HEADERS ──
//
// The one deliberate difference, and it fixes a latent bug rather than
// redesigning anything. Beta's loop reads each local file header and takes
// `compSize` from it — but when a writer sets the data-descriptor flag (bit 3),
// the local header's sizes are ZERO by specification and the real ones live in a
// descriptor AFTER the data. Its loop then reads a zero-length entry and
// desynchronises, and its own comment concedes the walk is only "sufficient for
// XLSX".
//
// The central directory always carries the true sizes, which is why the
// project's own test helper (`parsers/testkit.readZipEntries`) already reads it
// that way. Matching the helper the tests trust beats matching the loop the
// tests do not exercise.
// =============================================================================

/** End of central directory record. */
const EOCD_SIG = 0x06054b50;
const CDH_SIG = 0x02014b50;
/** The largest a ZIP comment can be, so the EOCD scan is bounded. */
const MAX_COMMENT = 0xffff;

/**
 * ⚠ A `ReadableStream` rather than `new Blob([bytes]).stream()`, which is what
 * the old client uses. Blob's `.stream()` is a browser convenience that jsdom
 * does not implement, so the Blob route makes this layer untestable outside a
 * real browser — and an unzipper is precisely the code that needs a test against
 * a real file. Same bytes, same decompressor, one fewer object.
 */
async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  const source = new ReadableStream<BufferSource>({
    start(c) { c.enqueue(bytes as unknown as BufferSource); c.close(); },
  });
  const stream = source.pipeThrough<Uint8Array>(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export interface ZipEntries { [name: string]: Uint8Array }

/**
 * Every stored/deflated entry, keyed by its path inside the archive.
 *
 * ⚠ Throws with a NAMED reason rather than returning a partial archive. A
 * half-read workbook produces a sheet with some rows in it, which imports as a
 * short but plausible set of trades — the failure mode this whole import path is
 * built to avoid.
 */
export async function readZip(bytes: Uint8Array): Promise<ZipEntries> {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error(
      'This browser cannot decompress ZIP data (no DecompressionStream), so .xlsx '
      + 'cannot be read here. Export as CSV instead.',
    );
  }

  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const dec = new TextDecoder();

  // ── find the End Of Central Directory ────────────────────────────────────
  // ⚠ Scanned BACKWARDS and bounded. The EOCD is last but may be followed by a
  // comment of up to 64 KB, so its position is not fixed — and a forward scan
  // for the signature can hit the same four bytes inside compressed data.
  let eocd = -1;
  const floor = Math.max(0, bytes.length - MAX_COMMENT - 22);
  for (let i = bytes.length - 22; i >= floor; i--) {
    if (dv.getUint32(i, true) === EOCD_SIG) { eocd = i; break; }
  }
  if (eocd < 0) {
    throw new Error('That file is not a readable ZIP archive — no end-of-directory record.');
  }

  const count = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);

  const out: ZipEntries = {};
  for (let n = 0; n < count; n++) {
    if (p + 46 > bytes.length || dv.getUint32(p, true) !== CDH_SIG) {
      throw new Error(`ZIP directory is corrupt at entry ${n + 1} of ${count}.`);
    }
    const method = dv.getUint16(p + 10, true);
    const compSize = dv.getUint32(p + 20, true);
    const nameLen = dv.getUint16(p + 28, true);
    const extraLen = dv.getUint16(p + 30, true);
    const commentLen = dv.getUint16(p + 32, true);
    const localOff = dv.getUint32(p + 42, true);
    const name = dec.decode(bytes.subarray(p + 46, p + 46 + nameLen));

    // ⚠ The LOCAL header's name/extra lengths, not the directory's. They differ
    // in practice — writers routinely put a Zip64 or timestamp extra field in
    // one and not the other — and using the wrong pair lands `dataStart` a few
    // bytes off, which inflates to garbage rather than failing.
    const lNameLen = dv.getUint16(localOff + 26, true);
    const lExtraLen = dv.getUint16(localOff + 28, true);
    const dataStart = localOff + 30 + lNameLen + lExtraLen;
    const raw = bytes.subarray(dataStart, dataStart + compSize);

    if (method === 0) out[name] = raw;
    else if (method === 8) out[name] = await inflateRaw(raw);
    else throw new Error(`Unsupported ZIP compression (method ${method}) for ${name}.`);

    p += 46 + nameLen + extraLen + commentLen;
  }

  return out;
}

export interface XlsxParts {
  /** EVERY worksheet, in workbook order. See below for why not just the first. */
  sheets: string[];
  sharedStrings: string | null;
}

/**
 * The XML parts an XLSX reader needs.
 *
 * ── 🚨 EVERY SHEET, NOT THE FIRST ONE ──
 *
 * The first version of this returned one sheet, and it would have been wrong on
 * the real export. Josh's ATAS workbook has THREE worksheets, and which of them
 * holds the trades is not positional — the old client walks all of them
 * (`for (let i = 1; i <= 20; i++)`), classifies each by its headers, and picks.
 * Handing back `sheets[0]` would have imported a statistics summary, or nothing,
 * from a file full of fills.
 *
 * ⚠ Sorted NUMERICALLY and not assumed to start at 1. String order puts
 * `sheet10` before `sheet2` — the wrong sheet rather than no sheet, which is the
 * quieter failure — and a workbook whose first sheet was deleted and re-added
 * has no `sheet1.xml` at all.
 */
export function xlsxParts(entries: ZipEntries): XlsxParts {
  const dec = new TextDecoder();
  const names = Object.keys(entries)
    .filter((n) => /^xl\/worksheets\/sheet\d+\.xml$/i.test(n))
    .sort((a, b) => {
      const na = Number(/(\d+)/.exec(a)?.[1] ?? 0);
      const nb = Number(/(\d+)/.exec(b)?.[1] ?? 0);
      return na - nb;
    });

  if (!names.length) {
    throw new Error(
      'That .xlsx contains no worksheet — the archive opened but has no '
      + 'xl/worksheets/sheetN.xml part.',
    );
  }

  const ss = entries['xl/sharedStrings.xml'];
  return {
    sheets: names.map((n) => dec.decode(entries[n] as Uint8Array)),
    // ⚠ Genuinely optional: a sheet with only inline strings and numbers has no
    // shared-string table, and demanding one would reject a valid workbook.
    sharedStrings: ss ? dec.decode(ss) : null,
  };
}
